import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-policy">
      <PublicNavbar>
        <Link to="/terms-and-conditions">Terms</Link>
        <Link to="/refund-policy">Refunds</Link>
        <Link to="/customer/login" className="nav-btn nav-btn-customer">Promoter Login</Link>
        <Link to="/admin/login" className="nav-btn nav-btn-admin">Admin Login</Link>
      </PublicNavbar>

      <main className="policy-content">
        <div className="container">
          <h1>Privacy Policy &ndash; TappyTray</h1>
          <p className="last-updated"><strong>Last Updated: 15 March 2026</strong></p>

          <p>
            TappyTray is operated by BoilingCode LLP ("Company", "we", "our", or "us"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use the TappyTray mobile application, website, and related services (collectively referred to as the "Platform").
          </p>
          <p>
            TappyTray is designed to help residential communities manage society activities, payments, services, and local business promotions through a unified digital platform.
          </p>
          <p>
            By accessing or using the Platform, you agree to the terms described in this Privacy Policy.
          </p>

          <section>
            <h2>1. Information We Collect</h2>
            <p>We collect different types of information to provide and improve our services.</p>

            <h3>Profile Information</h3>
            <p>When you register or use the Platform, we collect the following required information:</p>
            <ul>
              <li>Full name</li>
              <li>Mobile number (primary and optional secondary)</li>
              <li>Email address</li>
              <li>Gender</li>
              <li>Address details</li>
              <li>Society name and flat number</li>
            </ul>
            <p>This information helps us verify residents and provide community services.</p>

            <h3>Personal Information (Optional)</h3>
            <p>You may optionally provide additional personal details to enhance your experience:</p>
            <ul>
              <li>Profile photo</li>
              <li>Date of birth</li>
              <li>Marital status</li>
              <li>Occupation</li>
              <li>Income range</li>
              <li>Family size, number of children, and age of children</li>
            </ul>
            <p>Providing this information is entirely voluntary and helps us personalize the experience.</p>

            <h3>Interests and Preferences (Optional)</h3>
            <p>You may optionally share your interests and preferences to receive relevant content and promotions:</p>
            <ul>
              <li>Food and grocery preferences</li>
              <li>Fitness and sports</li>
              <li>Travel</li>
              <li>Electronics and gadgets</li>
              <li>Fashion</li>
              <li>Kids education</li>
              <li>Home decor</li>
              <li>Events and community activities</li>
            </ul>

            <h3>Household and Lifestyle Information (Optional)</h3>
            <p>You may optionally provide household details to enable better community features:</p>
            <ul>
              <li>Flat type (1BHK, 2BHK, 3BHK, Villa, etc.)</li>
              <li>Flat size (in sq. ft.)</li>
              <li>Ownership status (own or rent)</li>
              <li>Vehicle ownership details (car, bike, EV) and vehicle registration number</li>
              <li>Pet ownership details</li>
              <li>Domestic help information (maid, cook, driver)</li>
              <li>Work-from-home status</li>
            </ul>

            <h3>Society and Community Information</h3>
            <p>To enable society management features, we may collect:</p>
            <ul>
              <li>Society name</li>
              <li>Block or tower information</li>
              <li>Flat details (floor, flat number)</li>
              <li>Committee member information</li>
              <li>Maintenance billing data</li>
              <li>Community announcements and notices</li>
              <li>Visitor and guest records (name, phone, purpose of visit)</li>
              <li>Daily help records (helper name, type, and schedule)</li>
            </ul>

            <h3>Transaction Information</h3>
            <p>If you use the Platform for payments or service bookings, we may collect:</p>
            <ul>
              <li>Transaction history</li>
              <li>Maintenance payment details</li>
              <li>Service booking records</li>
              <li>Order details from local businesses</li>
            </ul>
            <p>
              Payment processing may be handled through secure third-party payment gateways such as Razorpay or Cashfree. We do not store sensitive card or banking information on our servers.
            </p>

            <h3>Device and Technical Information</h3>
            <p>When you use the Platform, we may automatically collect:</p>
            <ul>
              <li>Device type and operating system</li>
              <li>IP address</li>
              <li>App usage data</li>
              <li>Crash reports</li>
              <li>Log files and analytics information</li>
            </ul>
            <p>This helps us improve app performance and reliability.</p>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Create and manage user accounts</li>
              <li>Verify residents within societies</li>
              <li>Enable society management features</li>
              <li>Facilitate maintenance payments</li>
              <li>Provide service bookings and order management</li>
              <li>Send society announcements and updates</li>
              <li>Deliver relevant promotions from businesses</li>
              <li>Improve the functionality and security of the Platform</li>
              <li>Provide customer support</li>
            </ul>
          </section>

          <section>
            <h2>3. Promotions and Advertising</h2>
            <p>
              TappyTray enables local businesses and promoters to offer products, services, and promotions to residents within participating societies.
            </p>
            <p>These promotions may be shown based on:</p>
            <ul>
              <li>Society location</li>
              <li>User preferences</li>
              <li>Community activity</li>
            </ul>
            <p>We do not sell personal data to advertisers. Promotions are displayed within the Platform environment.</p>
          </section>

          <section>
            <h2>4. Sharing of Information</h2>
            <p>We may share information in the following situations:</p>

            <h3>Society Management</h3>
            <p>
              Resident information may be visible to authorized society administrators or committee members for verification and management purposes.
            </p>

            <h3>Service Providers</h3>
            <p>
              Information necessary to deliver services may be shared with vendors such as electricians, laundry providers, delivery partners, or other service professionals.
            </p>

            <h3>Payment Processing</h3>
            <p>
              Payment-related data may be shared with secure payment gateway providers to process transactions.
            </p>

            <h3>Legal Requirements</h3>
            <p>
              We may disclose information if required by law, court order, or government authority.
            </p>

            <p><strong>We do not sell personal user data to third parties.</strong></p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>We implement appropriate security measures to protect user information, including:</p>
            <ul>
              <li>Secure HTTPS connections</li>
              <li>Encrypted data transmission</li>
              <li>Controlled access to sensitive data</li>
              <li>Secure cloud infrastructure</li>
            </ul>
            <p>Despite these precautions, no digital platform can guarantee absolute security.</p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain personal information only for as long as necessary to:</p>
            <ul>
              <li>Provide services to users</li>
              <li>Maintain transaction records</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>
            <p>Users may request deletion of their account and associated data.</p>
          </section>

          <section>
            <h2>7. User Rights and Choices</h2>
            <p>Users have the right to:</p>
            <ul>
              <li>Access and update their profile information</li>
              <li>Request correction of inaccurate data</li>
              <li>Disable promotional notifications</li>
              <li>Request deletion of their account — <Link to="/delete-account">click here to request account deletion</Link></li>
            </ul>
            <p>Requests can be submitted through the contact information provided below, or use our <Link to="/delete-account">account deletion request form</Link>.</p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>
              TappyTray is not intended for individuals under the age of 18. We do not knowingly collect personal data from minors.
            </p>
            <p>
              If we become aware that a minor has submitted personal information, we will take steps to delete such data.
            </p>
          </section>

          <section>
            <h2>9. Third-Party Services</h2>
            <p>
              The Platform may integrate with third-party services including payment gateways, analytics tools, and service providers. These third parties may have their own privacy policies governing the use of information.
            </p>
            <p>We encourage users to review those policies where applicable.</p>
          </section>

          <section>
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our services or legal requirements.
            </p>
            <p>
              Users will be notified of significant changes through the Platform or via email where applicable.
            </p>
            <p>
              Continued use of the Platform after such updates constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2>11. Contact Information</h2>
            <div className="contact-info">
              <p>If you have any questions or concerns about this Privacy Policy, you may contact us at:</p>
              <p><strong>BoilingCode LLP</strong><br />Operator of TappyTray</p>
              <p><strong>Email</strong>: <a href="mailto:yogita@boilingcode.com">yogita@boilingcode.com</a></p>
              <p><strong>Website</strong>: <a href="https://tappytray.com">www.tappytray.com</a></p>
            </div>
          </section>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 BoilingCode LLP. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
            <Link to="/refund-policy">Refund Policy</Link>
            <Link to="/delete-account">Delete Account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

